import React, { useState, useEffect } from "react";
import { Row, Col, Card, Container } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom"; // 👈 add this
import Breadcrumbs from "../../../shared/components/Breadcrumbs";
import { TICKETING, TICKETING_KPI_DASHBOARD } from "../../../shared/constants/Constants";
import IndividualMetrics from "./components/IndividualMetrics";
import TeamMetrics from "./components/TeamMetrics";
import OrganizationMetrics from "./components/OrganizationMetrics";
import TimePeriodFilter from "./components/TimePeriodFilter";
import {
  loadTicketDashboardIndividualRequest,
  loadTicketDashboardTeamRequest,
  loadTicketDashboardOrganizationRequest,
} from "./redux/Actions";
import "./KPIDashboard.css";
import Permissions from "../../../shared/components/Permissions";

const TicketingKPIDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();

  let emp_no = JSON.parse(localStorage.getItem("emp_no"));
  let permission = Permissions(emp_no, "TS Org Metrics");
  console.log("Permissions", permission);

  // ─── Read initial state FROM query params ───────────────────────────────────
  const getInitialState = () => {
    const params = new URLSearchParams(location.search);
    const teamId = params.get("team") || null;
    const employeeId = params.get("employee_id") || null;

    let view = "individual";
    if (teamId) view = "team";
    if (employeeId) view = "individual";

    return { teamId, employeeId, view };
  };

  const initial = getInitialState();
  const [selectedPeriod, setSelectedPeriod] = useState("daily");
  const [selectedView, setSelectedView] = useState(initial.view);
  const [selectedTeamId, setSelectedTeamId] = useState(initial.teamId);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(initial.employeeId);
  const [selectedEmployeeName, setSelectedEmployeeName] = useState(null);


  const dispatch = useDispatch();
  const { individualMetrics, teamMetrics, organizationMetrics, loading } = useSelector(
    (store) => store.TicketDashboardMetrics
  );

  // ─── Sync state → URL ───────────────────────────────────────────────────────
  const updateURL = (view, teamId, employeeId) => {
    const params = new URLSearchParams();
    if (view === "team" && teamId) params.set("team", teamId);
    if (view === "individual" && employeeId) params.set("employee_id", employeeId);
    navigate({ search: params.toString() }, { replace: true });
  };

  // ─── Handlers ───────────────────────────────────────────────────────────────
  const handleTeamClick = (teamId) => {
    setSelectedTeamId(teamId);
    setSelectedEmployeeId(null);
    setSelectedView("team");
    updateURL("team", teamId, null);
  };

  const handleEmployeeClick = (employeeId, employeeName) => {
    setSelectedEmployeeId(employeeId);
    setSelectedEmployeeName(employeeName);
    setSelectedView("individual");
    updateURL("individual", null, employeeId);
  };

  const handleViewChange = (view) => {
  if (view === "individual") {
    setSelectedEmployeeId(null);
    setSelectedEmployeeName(null);
    setSelectedTeamId(null);
    setSelectedView("individual");
    navigate({ search: "" }, { replace: true }); 
  } else if (view === "team") {
    setSelectedTeamId(null);
    setSelectedEmployeeId(null);
    setSelectedEmployeeName(null);
    setSelectedView("team");
    navigate({ search: "" }, { replace: true }); 
  } else {
    setSelectedView("organization");
    navigate({ search: "" }, { replace: true }); 
  }
};

  const getPeriodLabel = (period) => {
    const labels = {
      daily: "Today",
      weekly: "This Week",
      monthly: "This Month",
      quarterly: "This Quarter",
      yearly: "This Year",
    };
    return labels[period] || period;
  };

  // ─── Dispatch API calls ──────────────────────────────────────────────────────
  useEffect(() => {
    if (selectedView === "individual") {
      dispatch(loadTicketDashboardIndividualRequest({
        period: selectedPeriod,
        employeeId: selectedEmployeeId,   // null = own metrics, id = specific employee
      }));
    } else if (selectedView === "team") {
      dispatch(loadTicketDashboardTeamRequest({
        period: selectedPeriod,
        teamId: selectedTeamId,           // null = default team, id = specific team
      }));
    } else if (selectedView === "organization") {
      dispatch(loadTicketDashboardOrganizationRequest({ period: selectedPeriod }));
    }
  }, [selectedView, selectedPeriod, selectedTeamId, selectedEmployeeId]);

  return (
    <Container fluid>
      <Row className="detailsPageHeader align-items-center">
        <Col>
          <Breadcrumbs
            children={[
              { href: "/ticketing/", label: TICKETING },
              { href: "/ticketing/kpi-dashboard", label: TICKETING_KPI_DASHBOARD },
            ]}
          />
        </Col>
      </Row>

      <Row className="mt-3">
        <Col>
          <Card className="shadow-sm">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-4">
                <div className="kpi-view-selector">
                  <button
                    className={`kpi-view-btn ${selectedView === "individual" ? "active" : ""}`}
                    onClick={() => handleViewChange("individual")}
                  >
                    <i className="fas fa-user me-2"></i>Individual
                  </button>
                  <button
                    className={`kpi-view-btn ${selectedView === "team" ? "active" : ""}`}
                    onClick={() => handleViewChange("team")}
                  >
                    <i className="fas fa-users me-2"></i>Team
                  </button>
                  {permission?.create_permission && (              
                    <button
                      className={`kpi-view-btn ${selectedView === "organization" ? "active" : ""}`}
                      onClick={() => handleViewChange("organization")}
                    >
                      <i className="fas fa-building me-2"></i>Organization
                    </button>
                  )}
                </div>
                <TimePeriodFilter
                  selectedPeriod={selectedPeriod}
                  onPeriodChange={setSelectedPeriod}
                />
              </div>

              {selectedView === "individual" && (
                <IndividualMetrics
                  period={selectedPeriod}
                  reduxData={individualMetrics}
                  loading={loading}
                  getPeriodLabel={getPeriodLabel}
                  employeeId={selectedEmployeeId} 
                  employeeName={selectedEmployeeName}  
                />
              )}
              {selectedView === "team" && (
                <TeamMetrics
                  period={selectedPeriod}
                  reduxData={teamMetrics}
                  loading={loading}
                  getPeriodLabel={getPeriodLabel}
                  onEmployeeClick={handleEmployeeClick}  
                />
              )}
              {selectedView === "organization" && (
                <OrganizationMetrics
                  period={selectedPeriod}
                  reduxData={organizationMetrics}
                  loading={loading}
                  getPeriodLabel={getPeriodLabel}
                  onTeamClick={handleTeamClick}          
                />
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default TicketingKPIDashboard;